import { useState } from "react";
import { Notify, toast } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateTimeField } from "@/components/ui/datetime-field";
import { Select } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useLines } from "@/features/lines/hooks";
import { useManualEntry } from "@/features/production/hooks";
import { useLineScrapByMachine, useLineTrackedByMachine } from "@/hooks/useLineTrackedByMachine";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { ImportWizard } from "@/features/data-import/ImportWizard";

type Tab = "manual" | "import";

export function DataEntryPage() {
  const [tab, setTab] = useState<Tab>("manual");
  const linesQuery = useLines();
  const { data: lines = [] } = linesQuery;
  const [lineId, setLineId] = useState<string>("");
  const [producedQty, setProducedQty] = useState<string>("");
  const [rejectedQty, setRejectedQty] = useState<string>("");
  const [observation, setObservation] = useState<string>("");
  const [referenceAt, setReferenceAt] = useState<string | null>(null);

  const manualEntry = useManualEntry(lineId);
  // No modo por-máquina o "Produzido" line-level some (entrada vai p/ a máquina).
  const trackedByMachine = useLineTrackedByMachine(lineId);
  // Spec 081 — o refugo tem a SUA pergunta: a rota o recusa quando existe máquina que sabe recebê-lo
  // (`lineHasScrapCounter`). Oferecer o campo com a pergunta da produção fazia o operador digitar
  // um número que voltava 409.
  const scrapByMachine = useLineScrapByMachine(lineId);

  async function submit() {
    if (!lineId) {
      toast.error("Selecione uma linha.");
      return;
    }
    const payload: Record<string, unknown> = {};
    if (producedQty && !trackedByMachine) payload.producedQty = Number(producedQty);
    if (rejectedQty && !scrapByMachine) payload.rejectedQty = Number(rejectedQty);
    if (observation.trim()) payload.observation = observation.trim();
    if (referenceAt) payload.referenceAt = referenceAt;

    if (Object.keys(payload).length === 0) {
      toast.error("Informe ao menos uma quantidade ou observação.");
      return;
    }

    try {
      await manualEntry.mutateAsync(payload);
      toast.success("Entrada registrada.");
      setProducedQty("");
      setRejectedQty("");
      setObservation("");
      setReferenceAt(null);
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao registrar entrada.", { description: msg });
    }
  }

  return (
    <section>
      <PageHeader
        title="Entrada de Dados"
        subtitle="Registro manual avulso ou importação em lote (CSV/XLSX)."
      />

      <nav className="mb-4 flex gap-2" aria-label="Tabs">
        <Button
          variant={tab === "manual" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("manual")}
        >
          Manual
        </Button>
        <Button
          variant={tab === "import" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("import")}
        >
          Importar arquivo
        </Button>
      </nav>

      {tab === "manual" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Registro manual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="line">Linha</Label>
                {linesQuery.isError ? (
                  <Notify.QueryError
                    error={linesQuery.error}
                    onRetry={() => void linesQuery.refetch()}
                    title="Não foi possível carregar as linhas"
                    variant="inline"
                  />
                ) : (
                  <Select id="line" value={lineId} onChange={(e) => setLineId(e.target.value)}>
                    <option value="">Selecione…</option>
                    {lines.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
              <div className="space-y-1.5">
                <DateTimeField
                  id="ref"
                  label="Data/hora de referência (opcional)"
                  value={referenceAt}
                  onChange={setReferenceAt}
                />
              </div>
              {trackedByMachine ? (
                <div className="space-y-1.5">
                  <Label>Produzido (un)</Label>
                  <p className="rounded-md border border-info/30 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                    Esta linha registra produção por máquina — informe a produção na máquina.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="prod">Produzido (un)</Label>
                  <Input
                    id="prod"
                    type="number"
                    min={0}
                    step="any"
                    placeholder="0"
                    value={producedQty}
                    onChange={(e) => setProducedQty(e.target.value)}
                  />
                </div>
              )}
              {scrapByMachine ? (
                <div className="space-y-1.5">
                  <Label>Rejeitado (un)</Label>
                  <p className="rounded-md border border-info/30 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                    Esta linha registra refugo por máquina — informe o refugo na máquina.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="rej">Rejeitado (un)</Label>
                  <Input
                    id="rej"
                    type="number"
                    min={0}
                    step="any"
                    placeholder="0"
                    value={rejectedQty}
                    onChange={(e) => setRejectedQty(e.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="obs">Observação</Label>
              <Input
                id="obs"
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                maxLength={1000}
                placeholder="Ex: ajuste de calibração durante o turno"
              />
            </div>
            <div className="rounded-md border border-info/30 bg-info/5 p-2 text-xs">
              Se houver uma execução ativa na linha, os valores serão incrementados nela. Caso
              contrário, o registro é apenas histórico.
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => void submit()}
                disabled={manualEntry.isPending}
                className={cn(manualEntry.isPending && "opacity-60")}
              >
                {manualEntry.isPending ? "Registrando…" : "Registrar"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <ImportWizard />
      )}
    </section>
  );
}
